import { WebAppManifest } from 'web-app-manifest';
import InAppSpy from 'inapp-spy';
import { IRelatedApp, IWebInstallNavigator, Manifest } from './types/types';

type EventDetails = Record<string, unknown>;

const _eventDispatcher = (_element: Element, name: string, message: string, details: EventDetails = {}) => {
    const event  = new CustomEvent(name, {
        detail: {
                    message,
                    ...details
        }
    });
    _element.dispatchEvent(event);
}
export default class Utils {
    static isWebInstallSupported(): boolean {
        return typeof (navigator as IWebInstallNavigator).install === 'function';
    }

    static resolveManifestUrl(manifestUrl: string): string {
        return new URL(manifestUrl, document.baseURI).href;
    }

    static isCurrentManifestTarget(manifestUrl: string): boolean {
        if (!manifestUrl)
            return true;

        const manifestLink = document.querySelector<HTMLLinkElement>('link[rel~="manifest"][href]');
        if (!manifestLink)
            return false;

        try {
            return this.resolveManifestUrl(manifestUrl) === manifestLink.href;
        } catch (error) {
            return false;
        }
    }

    static isAppleMobile(): boolean {
		if (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
			return true;
		return false;
    }

    static isServiceWorkerSupported(): boolean {
        return 'serviceWorker' in navigator;
    }

    static isInAppBrowser(): boolean {
        return InAppSpy().isInApp;
    }

    static async copyTextToClipboard(text: string): Promise<boolean> {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            return false;
        }
    }

    static isAppleDesktop(): boolean {
        // check if it's a mac
        const userAgent = navigator.userAgent.toLowerCase();
        if (navigator.maxTouchPoints || !userAgent.match(/macintosh/))
            return false;
        // check safari version >= 17
        const version = /version\/(\d{2})\./.exec(userAgent)
        if (!version || !version[1] || !(parseInt(version[1]) >= 17))
            return false;
        // hacky way to detect Sonoma
        const audioCheck = document.createElement('audio').canPlayType('audio/wav; codecs="1"') ? true : false;
        const webGLCheck = new OffscreenCanvas(1, 1).getContext('webgl') ? true : false;

        return audioCheck && webGLCheck;
    }

    static isAppleMobileNonSafari(): boolean {
        return this.isAppleMobile() && /CriOS|EdgiOS|FxiOS|Brave|Ddg|OPT|YaBrowser/.test(navigator.userAgent);
    }

    static isYaBrowser(): boolean {
        return /YaBrowser/.test(navigator.userAgent);
    }

    static isDuck(): boolean {
        return /Ddg/.test(navigator.userAgent);
    }

    static isOpera(): boolean {
        return /OPT/.test(navigator.userAgent);
    }

    static isFox(): boolean {
        return /FxiOS/.test(navigator.userAgent);
    }

    static isEdge(): boolean {
        return /EdgiOS/.test(navigator.userAgent);
    }

    static isIPad(): boolean {
        return this.isAppleMobile() && /iPad|Macintosh/.test(navigator.userAgent);
    }

    static isIPhoneSafari(): boolean {
        return this.isAppleMobile() && !this.isIPad() && !this.isAppleMobileNonSafari();
    }

    static isApple26Plus(): boolean {
        return CSS.supports('text-wrap', 'pretty');
    }
    static isApple27Plus(): boolean {
        return CSS.supports('background', 'revert-rule');
    }

    static getPageBackgroundColor(): string | null {
        const bodyColor = window.getComputedStyle(document.body).backgroundColor;
        const htmlColor = window.getComputedStyle(document.documentElement).backgroundColor;
        return htmlColor !== 'rgba(0, 0, 0, 0)' ? htmlColor : bodyColor !== 'rgba(0, 0, 0, 0)' ? bodyColor : null;
    }

    static isAndroid(): boolean {
        return /android/.test(navigator.userAgent.toLowerCase());
    }

    static isAndroidFallback(): boolean {
        if (this.isAndroid() && !('BeforeInstallPromptEvent' in window))
            return true;
        return false;
    }

    static deviceFormFactor(): 'narrow' | 'wide' {
        return window.matchMedia('(orientation: portrait)').matches? 'narrow' : 'wide';
    }
    
    static isStandalone() {
		if (window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone === true))
			return true;
		return false;
    }

    static async getInstalledRelatedApps(): Promise<IRelatedApp[]> {
        if ('getInstalledRelatedApps' in navigator)
            try{
                return await (navigator as any).getInstalledRelatedApps().then((relatedApps: IRelatedApp[]) => {
                    return relatedApps;
                });
            } catch (e) {}
            
        return [];
    }
    
    static async isRelatedAppsInstalled(): Promise<boolean> {
        const _relatedApps = await this.getInstalledRelatedApps();
        return _relatedApps.length? true : false;
    }

    static setStorageFlag(name: string, value:boolean, persistent: boolean = false) {
        try {
            if (persistent)
                localStorage.setItem(name, value.toString());
            else
                sessionStorage.setItem(name, value.toString());
        } catch (e) {}
    }
    static getStorageFlag(name: string): boolean {
        try {
            return sessionStorage.getItem(name) === 'true' || localStorage.getItem(name) === 'true';
        } catch (e) {
            return false;
        }
    }

    static eventInstalledSuccess(_element: Element, details: EventDetails = {}) {
        _eventDispatcher(_element, 'pwa-install-success-event', 'App install success (Chromium/Android only)', details);
    }
    static eventInstalledFail(_element: Element, details: EventDetails = {}) {
        _eventDispatcher(_element, 'pwa-install-fail-event', 'App install failed (Chromium/Android only)', details);
    }
    static eventInstallBackend(_element: Element, backend: 'web-install' | 'beforeinstallprompt', reason?: 'missing-manifest-id' | 'web-install-failed' | 'web-install-unavailable') {
        let message = backend === 'web-install'
            ? 'Install requested with Web Install API.'
            : 'Install requested with beforeinstallprompt.';
        if (reason === 'missing-manifest-id')
            message = 'Web Install API skipped because neither manifest-id nor an id in the fetched manifest is available. Falling back to beforeinstallprompt.';
        else if (reason === 'web-install-failed')
            message = 'Web Install API previously failed. Falling back to beforeinstallprompt.';
        else if (reason === 'web-install-unavailable')
            message = 'Web Install API is unavailable. Using beforeinstallprompt.';
        _eventDispatcher(_element, 'pwa-install-backend-event', message, {
            backend,
            ...(reason ? {reason} : {})
        });
    }
    static eventUserChoiceResult(_element: Element, message: string) {
        _eventDispatcher(_element, 'pwa-user-choice-result-event', message);
    }
    static eventInstallAvailable(_element: Element) {
        _eventDispatcher(_element, 'pwa-install-available-event', 'App install available');
    }
    static eventInstallHowTo(_element: Element) {
        _eventDispatcher(_element, 'pwa-install-how-to-event', 'App install instruction showed');
    }
    static eventGallery(_element: Element) {
        _eventDispatcher(_element, 'pwa-install-gallery-event', 'App install gallery showed');
    }

    static normalizeManifestAssetUrls(manifest: WebAppManifest, manifestUrl: string) {
        const normalizedManifestUrl = new URL(manifestUrl, document.location.href);
        [...manifest.icons || [], ...manifest.screenshots || []].forEach(asset => {
            asset.src = new URL(asset.src, normalizedManifestUrl).href;
        })
    }

    static async fetchAndProcessManifest(manifestUrl: string, icon: string, name: string, description: string): Promise<{_manifest: WebAppManifest, icon: string, name: string, description: string}> {
        let _manifest: WebAppManifest = new Manifest();
        let _json: WebAppManifest | null = null;
        try{
			const _response = await fetch(manifestUrl);
			_json = await _response.json() as WebAppManifest;
			if (!_response.ok || !_json || !Object.keys(_json))
				throw new Error('Manifest not found');
			this.normalizeManifestAssetUrls(_json, manifestUrl);
        }  
        catch(e) {}    

        icon = icon || (_json?.icons?.length ? _json?.icons![0].src : _manifest.icons?.[0].src) || '';
        name = name || (_json? _json['short_name']: _manifest['short_name']) || '';
        description = description || _json?.description || _manifest.description || '';
		_manifest = _json || _manifest;
        
        return {
            _manifest,
            icon,
            name,
            description
        }
    }
}